import * as React from 'react';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { connect } from 'react-redux';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getFileLink, getDefaultFileList } from '../../utils/FileAccessUtils';
import { buildConvertedImageIndex, resolveAttachment } from '../../utils/ConvertedImageUtils';
import { EmptyState } from '../empty_state/EmptyState';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import OWUpload from '../OWUpload';
import isEqual from 'lodash/isEqual';

require('./FileUploader.less');

export function fileUploadValidation(file, fileMimeType) {
	const fileMimeTypes = [fileMimeType];
	const isFileType = fileMimeTypes.indexOf(file.type) !== -1;
	if (!isFileType) {
		message.error(`Uploaded file is not of the expected type: ${fileMimeType}`);
	}
	return isFileType;
}

class FileUploader extends React.Component<any, any> {
	constructor(props) {
		super(props);
		const initialDefaultList = props.defaultFileList || [];
		const defaultFileList = getDefaultFileList(
			initialDefaultList,
			props.userType,
			props.convertedImages
		);
		this.state = {
			uploadLoading: false,
			fileList: defaultFileList,
		};
	}

	componentDidUpdate(prevProps) {
		const prevDefault = prevProps.defaultFileList || [];
		const nextDefault = this.props.defaultFileList || [];

		const defaultListChanged = !isEqual(prevDefault, nextDefault);
		// The conversions land after the attachment itself does — the Lambda runs once the upload
		// is in S3 — so a work order can re-render with the same file list and a newly populated
		// `convertedImages`. Without watching it too, that first render would keep showing the
		// undecodable original until something else forced a rebuild.
		const convertedImagesChanged = !isEqual(prevProps.convertedImages, this.props.convertedImages);

		if (defaultListChanged) {
			const defaultFileList = getDefaultFileList(
				nextDefault,
				this.props.userType,
				this.props.convertedImages
			);
			this.setState({ fileList: defaultFileList });
		} else if (convertedImagesChanged) {
			// Only the conversions moved, so re-point the entries already on screen rather than
			// rebuilding: `state.fileList` can hold an in-flight upload or a local removal that the
			// persisted `defaultFileList` does not know about yet, and a conversion lands seconds
			// after an upload — exactly when someone is mid-edit.
			this.setState(({ fileList }) => ({
				fileList: this.resolveUrlsAgainstConversions(fileList),
			}));
		}
	}

	// Points each uploaded entry at its derivative, or back at the original when there is none.
	// Label, link and thumbnail move together — via the same `resolveAttachment` the rebuild path
	// goes through — so a converted photo does not keep advertising a .HEIC name, and a thumbnail
	// left over from a conversion that has gone away is cleared rather than left stale.
	// Mutates in place, as antd holds on to these file objects to track progress.
	resolveUrlsAgainstConversions = (fileList) => {
		const { userType } = this.props;
		const convertedImageIndex = buildConvertedImageIndex(this.props.convertedImages);
		return fileList.map((file) => {
			if (file.response) {
				// Component will show file.url as link
				const fileId = nullSafeGetOrElse('response.data.fileId', file, '');
				const fileName = nullSafeGetOrElse('response.data.fileName', file, '');
				const resolved = resolveAttachment(
					{ fileId, fileName, s3ObjectKey: fileId && fileName ? `${fileId}/${fileName}` : null },
					convertedImageIndex
				);
				file.url = resolved.convertedUrl || getFileLink(fileId, fileName, userType);
				file.name = resolved.displayFileName || file.name;
				file.thumbUrl = resolved.convertedThumbUrl || undefined;
			}
			return file;
		});
	};

	handleUploadChange = (info) => {
		const { handleUploadSuccess, handleFileListChange } = this.props;

		let fileList = handleFileListChange ? handleFileListChange(info).fileList : info.fileList;

		// Any upload or removal re-runs this over the WHOLE list, so an entry whose derivative was
		// already resolved would have its URL rebuilt from the original and go undecodable again.
		// `response.data` stays pointed at the original, which is what gets persisted.
		fileList = this.resolveUrlsAgainstConversions(fileList);

		fileList = fileList.filter((file) => {
			if (file.response) {
				return file.status === 'done';
			}
			return true;
		});

		this.setState({ fileList });

		const basicFileList = fileList.map((file) => {
			const fileId = nullSafeGetOrElse('response.data.fileId', file, '');
			const fileName = nullSafeGetOrElse('response.data.fileName', file, '');
			return {
				fileId,
				fileName,
			};
		});

		if (basicFileList.filter((_) => !_.fileId).length === 0) {
			handleUploadSuccess(basicFileList);
		}
	};

	render() {
		const {
			uploaderType,
			emptyState,
			multiple,
			beforeUpload,
			userType,
			buttonType,
			accept,
			trigger,
		} = this.props;
		const { fileList } = this.state;

		const BACKEND_URI = getBackendUri();
		const token = retrieveCachedUserDetails(['token']).token;
		const uploadProps = {
			action: `${BACKEND_URI}/api/v1/${userType}/file/upload`,
			onChange: this.handleUploadChange,
			fileList: fileList,
			multiple: multiple,
			beforeUpload: beforeUpload,
			showUploadList: { showPreviewIcon: true, showRemoveIcon: true },
			headers: { 'X-Auth-Token': token },
			...(accept && { accept }),
		};

		// Read-only must not be able to upload. This used to be a copy of uploadProps with only the
		// remove icon hidden, which left a real hole: the empty-state graphic below is passed as
		// Upload's `children`, and antd only hides the clickable upload trigger when children are
		// absent (`renderUploadButton(children ? undefined : { display: 'none' })` in
		// antd/lib/upload/Upload.js). So with an empty file list the "Nothing's here" placeholder was
		// itself a working file picker, and handleUploadChange persisted whatever was dropped in.
		// Drop the upload wiring entirely: no file dialog on click, and no onChange, so even if a
		// trigger were reached nothing can be persisted. `action`/`headers` are omitted because
		// OWUpload's customRequest supersedes them anyway.
		const uploadReadOnlyProps = {
			className: 'fileUploaderReadOnly',
			fileList: fileList,
			multiple: multiple,
			showUploadList: { showPreviewIcon: true, showRemoveIcon: false },
			openFileDialogOnClick: false,
			// `openFileDialogOnClick: false` only drops the click/keyboard handlers - rc-upload still
			// wires onDrop unconditionally, so dragging a file onto the placeholder would run
			// OWUpload's customRequest and push the bytes to storage (nothing would attach, since
			// there is no onChange, but the request is still made). Returning false from
			// beforeUpload stops rc-upload before it posts.
			beforeUpload: () => false,
		};

		const uploadReadAndRemoveProps = {
			action: `${BACKEND_URI}/api/v1/${userType}/file/upload`,
			onChange: this.handleUploadChange,
			fileList: fileList,
			multiple: multiple,
			beforeUpload: beforeUpload,
			showUploadList: { showPreviewIcon: true, showRemoveIcon: true },
			headers: { 'X-Auth-Token': token },
			...(accept && { accept }),
		};

		return uploaderType === 'dragger' ? (
			<OWUpload uploaderType="dragger" {...uploadProps} multiple>
				<div style={{ marginTop: 32, height: 180 }}>
					<p className="ant-upload-drag-icon">
						<InboxOutlined />
					</p>
					<p className="ant-upload-text">Click or drag files to this area to upload</p>
					<p className="ant-upload-hint">Support for single or bulk upload.</p>
				</div>
			</OWUpload>
		) : uploaderType === 'readOnly' ? (
			<OWUpload {...uploadReadOnlyProps}>
				{fileList && fileList.length === 0 ? (
					emptyState ? (
						emptyState
					) : (
						<EmptyState
							height={160}
							graphic={
								<img
									style={{ marginBottom: 8 }}
									src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
									alt="Nothing's here."
								/>
							}
							headline={"Nothing's here."}
							body={
								<div style={{ textAlign: 'center' }}>
									<div style={{ maxWidth: 488, marginBottom: 16 }}>
										No files have been uploaded yet.
									</div>
								</div>
							}
						/>
					)
				) : null}
			</OWUpload>
		) : uploaderType === 'readAndRemove' ? (
			<OWUpload {...uploadReadAndRemoveProps}></OWUpload>
		) : trigger ? (
			<OWUpload {...uploadProps}>{trigger}</OWUpload>
		) : (
			<OWUpload {...uploadProps}>
				<Button type={buttonType}>
					<UploadOutlined /> Click to Upload
				</Button>
			</OWUpload>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	userType: state.session.userType,
	emptyState: ownProps.emptyState,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(FileUploader);
